import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { MapPin } from "lucide-react";
import { IMAGES, EXT, VILLA } from "../data";
import { useLang } from "../i18n";

export const Location = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="localisation" ref={ref} className="relative overflow-hidden" data-testid="location-section">
      <div className="relative h-[85vh] w-full overflow-hidden">
        <motion.img
          style={{ y, scale: 1.2 }}
          src={EXT.nightDining}
          alt="Terrasse et salle à manger extérieure de nuit"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/30 to-transparent" />

        <div className="relative z-10 h-full flex items-center px-6 md:px-12 max-w-[1600px] mx-auto">
          <div className="max-w-xl text-sand-bg">
            <span className="text-[11px] tracking-[0.35em] uppercase text-white/70">{t.location.eyebrow}</span>
            <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 mb-6 tracking-tight leading-[1.02]">
              {t.location.title}
            </h2>
            <p className="text-white/85 font-light text-base md:text-lg leading-relaxed mb-8">
              {t.location.body}
            </p>
            <a
              href={VILLA.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-map-link"
              className="inline-flex items-center gap-3 border border-white/50 text-white px-6 py-3 text-sm hover:bg-white hover:text-ink transition-colors duration-300"
            >
              <MapPin className="w-4 h-4" strokeWidth={1.4} /> {t.location.mapBtn}
            </a>
          </div>
        </div>
      </div>

      <div className="bg-sand-surface border-t border-[var(--line)]">
        <div className="grid grid-cols-2 md:grid-cols-4 max-w-[1600px] mx-auto">
          {t.location.facts.map((f, i) => {
            const Icon = Icons[f.icon] || Icons.MapPin;
            return (
              <div
                key={i}
                className="p-8 md:p-12 border-r border-[var(--line)] last:border-r-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r"
                data-testid={`location-fact-${i}`}
              >
                <Icon className="w-5 h-5 text-terracotta mb-4" strokeWidth={1.3} />
                <p className="text-ink-soft text-xs mb-1">{f.label}</p>
                <p className="font-serif text-2xl md:text-3xl text-ink font-light">{f.value}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
